/**
 * 菜谱卡片。
 *
 * 展示顺序刻意排成：菜名 → 耗时 → **缺什么** → 健康说明 → 做法。
 * 因为用户真正的决策点是"我现在缺几样、值不值得去买或换一道"，
 * 而不是"这道菜的做法多优雅"。
 *
 * 折叠用 grid-rows 过渡而不是 max-height 猜测值：
 * max-height 需要估一个足够大的数字，展开时会有空转的延迟感。
 */

import { useState, type ReactNode } from 'react';
import type { Recipe } from '@fridge/shared';

import { GLYPHS, IconGlyph } from './IconGlyph';
import { Pressable } from './Pressable';

export interface RecipeCardProps {
	recipe: Recipe;
	onToggleFavorite: (recipe: Recipe) => void;
	onPushMissing: (recipe: Recipe) => void;
	onDelete: (recipe: Recipe) => void;
}

export function RecipeCard({
	recipe,
	onToggleFavorite,
	onPushMissing,
	onDelete,
}: RecipeCardProps): JSX.Element {
	const [expanded, setExpanded] = useState(false);
	const missing = recipe.ingredients.filter((ingredient) => !ingredient.have);

	return (
		<article data-stagger className="surface overflow-hidden">
			<header className="flex items-start gap-3 p-4 pb-3">
				<div className="min-w-0 flex-1">
					<h3 className="text-[17px] font-semibold leading-snug text-label">{recipe.title}</h3>
					<p className="mt-1 text-[12.5px] leading-relaxed text-label-2">{recipe.summary}</p>
				</div>
				<Pressable
					aria-label={recipe.favorite ? '取消收藏' : '收藏'}
					onClick={() => onToggleFavorite(recipe)}
					scale={0.9}
					className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
				>
					<IconGlyph
						glyph={GLYPHS.favorite}
						size={19}
						weight={recipe.favorite ? 'fill' : 'regular'}
						className={recipe.favorite ? 'text-warn' : 'text-label-3'}
					/>
				</Pressable>
			</header>

			<div className="flex flex-wrap items-center gap-1.5 px-4">
				<Pill>
					<IconGlyph glyph={GLYPHS.clock} size={12} />
					<span className="tnum">{recipe.minutes} 分钟</span>
				</Pill>
				<Pill>
					<span className="tnum">{recipe.servings} 人份</span>
				</Pill>
				{recipe.flavorTags.slice(0, 2).map((tag) => (
					<Pill key={tag}>{tag}</Pill>
				))}
				{missing.length === 0 ? (
					<span className="rounded-control bg-ok-soft px-2 py-1 text-[11px] font-medium text-ok">
						材料齐了
					</span>
				) : (
					<span className="rounded-control bg-warn-soft px-2 py-1 text-[11px] font-medium text-warn">
						缺 {missing.length} 样
					</span>
				)}
			</div>

			{recipe.healthNote ? (
				<p className="mx-4 mt-3 rounded-input bg-surface-2 px-3 py-2 text-[12.5px] leading-relaxed text-label-2">
					{recipe.healthNote}
				</p>
			) : null}

			<ul className="mx-4 mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
				{recipe.ingredients.map((ingredient) => (
					<li
						key={`${ingredient.name}-${ingredient.amount}`}
						className={`text-[13px] ${ingredient.have ? 'text-label' : 'text-warn'}`}
					>
						{ingredient.have ? '' : '＋'}
						{ingredient.name}
						<span className="text-[11.5px] text-label-2"> {ingredient.amount}</span>
					</li>
				))}
			</ul>

			{/* grid-rows 0fr → 1fr 是唯一不需要猜高度的折叠动画 */}
			<div
				className="mx-4 grid transition-[grid-template-rows,opacity] duration-300 ease-[var(--ease-out)]"
				style={{
					gridTemplateRows: expanded ? '1fr' : '0fr',
					opacity: expanded ? 1 : 0,
				}}
			>
				<ol className="flex flex-col gap-2.5 overflow-hidden border-t border-separator pt-3">
					{recipe.steps.map((step) => (
						<li key={step.order} className="flex gap-2.5">
							<span className="tnum mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-[11px] text-label-2">
								{step.order}
							</span>
							<span className="text-[13.5px] leading-relaxed text-label">
								{step.text}
								{step.minutes ? (
									<span className="tnum ml-1 text-[11.5px] text-label-2">
										约 {step.minutes} 分钟
									</span>
								) : null}
							</span>
						</li>
					))}
				</ol>
			</div>

			<footer className="flex items-center gap-2 p-4 pt-3">
				<Pressable
					className="cta-quiet flex-1 py-2.5 text-[13px]"
					onClick={() => setExpanded((value) => !value)}
				>
					{expanded ? '收起做法' : '看做法'}
				</Pressable>
				{missing.length > 0 ? (
					<Pressable
						className="cta-quiet flex-1 py-2.5 text-[13px]"
						onClick={() => onPushMissing(recipe)}
					>
						缺的加进待买
					</Pressable>
				) : null}
				<Pressable
					aria-label="删除这道菜"
					onClick={() => onDelete(recipe)}
					className="flex h-10 w-10 items-center justify-center rounded-control bg-surface-2 text-label-2"
				>
					<IconGlyph glyph={GLYPHS.delete} size={16} />
				</Pressable>
			</footer>
		</article>
	);
}

function Pill({ children }: { children: ReactNode }): JSX.Element {
	return (
		<span className="inline-flex items-center gap-1 rounded-control bg-surface-2 px-2 py-1 text-[11px] text-label-2">
			{children}
		</span>
	);
}
